/*eslint-disable*/
import { HamburgerIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import IconBox from 'components/Icons/IconBox';
import { HSeparator } from 'components/Separator/Separator';
import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUserState } from 'context/UserContext';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useTranslation } from 'i18n';

const findActiveCollapse = (routes, pathname) => {
  for (const r of routes || []) {
    if (r.collapse && Array.isArray(r.views)) {
      const hit = r.views.some(
        v => v && !v.redirect && v.layout && v.path && `${v.layout}${v.path}` === pathname,
      );
      if (hit) return r.collapse;
    }
    if (r.category && Array.isArray(r.views)) {
      const nested = findActiveCollapse(r.views, pathname);
      if (nested) return nested;
    }
  }
  return '';
};

const countVisibleViews = views =>
  Array.isArray(views) ? views.filter(v => v && !v.redirect && v.path && !v.category).length : 0;

export function SidebarResponsive(props) {
  let location = useLocation();
  const { logo, routes, colorMode, hamburgerColor, ...rest } = props;
  const { userInfo } = useUserState();
  const { t } = useTranslation();

  const [state, setState] = React.useState({});
  const mainPanel = React.useRef();

  const [openGroups, setOpenGroups] = useState(() => {
    const active = findActiveCollapse(routes, location.pathname);
    return new Set(active ? [active] : []);
  });

  useEffect(() => {
    const active = findActiveCollapse(routes, location.pathname);
    if (active) {
      setOpenGroups(prev => {
        if (prev.size === 1 && prev.has(active)) return prev;
        return new Set([active]);
      });
    } else {
      setOpenGroups(prev => (prev.size === 0 ? prev : new Set()));
    }
  }, [location.pathname, routes]);

  const toggleGroup = collapse => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(collapse)) next.delete(collapse);
      else next.add(collapse);
      return next;
    });
  };

  const activeRoute = routeName => (location.pathname === routeName ? 'active' : '');

  let activeBg = useColorModeValue('white', 'navy.700');
  let inactiveBg = useColorModeValue('white', 'navy.700');
  let activeColor = useColorModeValue('gray.700', 'white');
  let inactiveColor = useColorModeValue('gray.400', 'white');
  let sidebarActiveShadow = useColorModeValue('0px 7px 11px rgba(0, 0, 0, 0.04)', 'none');
  let sidebarBackgroundColor = useColorModeValue('white', 'navy.800');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const badgeBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const badgeColor = useColorModeValue('gray.600', 'gray.200');
  // Hamburger button (mobile) — visible chip on the transparent navbar
  const hamburgerBtnBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const hamburgerBtnBg = useColorModeValue('white', 'whiteAlpha.100');
  const hamburgerBtnHoverBg = useColorModeValue('gray.50', 'whiteAlpha.200');
  const hamburgerBtnActiveBg = useColorModeValue('gray.100', 'whiteAlpha.300');

  const createLinks = routes => {
    return routes.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }
      if (prop.collapse) {
        const isOpen = openGroups.has(prop.collapse);
        const viewCount = countVisibleViews(prop.views);
        const groupLabel =
          document.documentElement.dir === 'rtl'
            ? prop.rtlName
            : prop.nameKey
            ? t(prop.nameKey)
            : prop.name;
        return (
          <React.Fragment key={key}>
            <Tooltip
              label={groupLabel}
              placement="right"
              hasArrow
              openDelay={400}
              gutter={12}
            >
            <Button
              boxSize="initial"
              justifyContent="flex-start"
              alignItems="center"
              bg="transparent"
              mb={{ xl: '6px' }}
              mx={{ xl: 'auto' }}
              py="12px"
              ps={{ sm: '10px', xl: '16px' }}
              borderRadius="15px"
              w="100%"
              pe={{ sm: '8px', xl: '10px' }}
              _hover={{ bg: hoverBg }}
              _active={{ bg: 'inherit', transform: 'none', borderColor: 'transparent' }}
              _focus={{ boxShadow: 'none' }}
              aria-expanded={isOpen}
              onClick={() => toggleGroup(prop.collapse)}
            >
              <Flex alignItems="center" w="100%" justifyContent="space-between" gap="6px">
                <Flex alignItems="center" minW={0} flex="1">
                  {typeof prop.icon === 'string' ? (
                    <Icon>{prop.icon}</Icon>
                  ) : (
                    <IconBox bg={inactiveBg} color="blue.500" h="30px" w="30px" me="12px">
                      {prop.icon}
                    </IconBox>
                  )}
                  <Text
                    color={inactiveColor}
                    my="auto"
                    fontSize="14px"
                    noOfLines={1}
                    minW={0}
                  >
                    {document.documentElement.dir === 'rtl'
                      ? prop.rtlName
                      : prop.nameKey
                      ? t(prop.nameKey)
                      : prop.name}
                  </Text>
                  {viewCount > 0 && (
                    <Badge
                      ml="8px"
                      px="6px"
                      py="2px"
                      borderRadius="full"
                      fontSize="10px"
                      fontWeight="semibold"
                      flexShrink={0}
                      bg={badgeBg}
                      color={badgeColor}
                      textTransform="none"
                    >
                      {viewCount}
                    </Badge>
                  )}
                </Flex>
                <Box color={inactiveColor} opacity={0.7} flexShrink={0}>
                  {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                </Box>
              </Flex>
            </Button>
            </Tooltip>
            <Collapse
              in={isOpen}
              animateOpacity
              unmountOnExit
              transition={{
                enter: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                exit: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
              }}
            >
              <Box ml={3} mr={3} mb={2}>
                {createLinks(prop.views)}
              </Box>
            </Collapse>
          </React.Fragment>
        );
      }
      if (prop.category) {
        var st = {};
        st[prop['state']] = !state[prop.state];
        return (
          <React.Fragment key={key}>
            <Text
              color={activeColor}
              fontWeight="bold"
              mb={{ xl: '6px' }}
              mx="auto"
              ps={{ sm: '10px', xl: '16px' }}
              py="12px"
            >
              {document.documentElement.dir === 'rtl'
                ? prop.rtlName
                : prop.nameKey
                ? t(prop.nameKey)
                : prop.name}
            </Text>
            {createLinks(prop.views)}
          </React.Fragment>
        );
      }
      return (
        <NavLink
          to={prop.layout + prop.path}
          key={key}
          {...(prop.layout === '/legal'
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
        >
          <Tooltip
            label={
              document.documentElement.dir === 'rtl'
                ? prop.rtlName
                : prop.nameKey
                ? t(prop.nameKey)
                : prop.name
            }
            placement="right"
            hasArrow
            openDelay={400}
            gutter={12}
          >
          {activeRoute(prop.layout + prop.path) === 'active' ? (
            <Button
              boxSize="initial"
              justifyContent="flex-start"
              alignItems="center"
              bg={activeBg}
              boxShadow={sidebarActiveShadow}
              mb={{ xl: '6px' }}
              mx={{ xl: 'auto' }}
              ps={{ sm: '10px', xl: '16px' }}
              py="12px"
              borderRadius="15px"
              w="100%"
              _active={{ bg: 'inherit', transform: 'none', borderColor: 'transparent' }}
              _focus={{ boxShadow: 'none' }}
            >
              <Flex>
                {typeof prop.icon === 'string' ? (
                  <Icon>{prop.icon}</Icon>
                ) : (
                  <IconBox bg="blue.500" color="white" h="30px" w="30px" me="12px">
                    {prop.icon}
                  </IconBox>
                )}
                <Text color={activeColor} my="auto" fontSize="14px">
                  {document.documentElement.dir === 'rtl'
                    ? prop.rtlName
                    : prop.nameKey
                    ? t(prop.nameKey)
                    : prop.name}
                </Text>
              </Flex>
            </Button>
          ) : (
            <Button
              boxSize="initial"
              justifyContent="flex-start"
              alignItems="center"
              bg="transparent"
              mb={{ xl: '6px' }}
              mx={{ xl: 'auto' }}
              py="12px"
              ps={{ sm: '10px', xl: '16px' }}
              borderRadius="15px"
              w="100%"
              _active={{ bg: 'inherit', transform: 'none', borderColor: 'transparent' }}
              _focus={{ boxShadow: 'none' }}
            >
              <Flex>
                {typeof prop.icon === 'string' ? (
                  <Icon>{prop.icon}</Icon>
                ) : (
                  <IconBox bg={inactiveBg} color="blue.500" h="30px" w="30px" me="12px">
                    {prop.icon}
                  </IconBox>
                )}
                <Text color={inactiveColor} my="auto" fontSize="14px">
                  {document.documentElement.dir === 'rtl'
                    ? prop.rtlName
                    : prop.nameKey
                    ? t(prop.nameKey)
                    : prop.name}
                </Text>
              </Flex>
            </Button>
          )}
          </Tooltip>
        </NavLink>
      );
    });
  };

  var links = <>{createLinks(routes)}</>;

  var brand = (
    <Box pt={'35px'} mb="8px">
      {logo}
      <HSeparator my="26px" />
    </Box>
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();

  return (
    <Flex display={{ sm: 'flex', xl: 'none' }} ref={mainPanel} alignItems="center">
      <Button
        ref={btnRef}
        onClick={onOpen}
        aria-label="Mở menu"
        variant="ghost"
        h="40px"
        minW="40px"
        px="0"
        ml="6px"
        borderRadius="10px"
        borderWidth="1px"
        borderColor={hamburgerBtnBorder}
        bg={hamburgerBtnBg}
        _hover={{ bg: hamburgerBtnHoverBg }}
        _active={{ bg: hamburgerBtnActiveBg }}
      >
        <HamburgerIcon color={hamburgerColor} w="20px" h="20px" />
      </Button>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement={document.documentElement.dir === 'rtl' ? 'right' : 'left'}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent
          w="280px"
          maxW="280px"
          ms={{ sm: '16px' }}
          my={{ sm: '16px' }}
          borderRadius="16px"
          bg={sidebarBackgroundColor}
        >
          <DrawerCloseButton _focus={{ boxShadow: 'none' }} _hover={{ boxShadow: 'none' }} />
          <DrawerBody maxW="280px" px="1rem">
            <Box maxW="100%" h="100vh">
              <Box>{brand}</Box>
              <Stack direction="column" mb="40px">
                <Box>{links}</Box>
              </Stack>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default SidebarResponsive;
